// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";
import { useNavigate } from "react-router-dom";

// material styles
import {
  Grid,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import {
  addRecordRequest,
  getRecordSubCategoryRequest,
} from "api/requests/records";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";

// helper
import Toast from "helper/toast";

// form validate
import { pageItemSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./addRecords.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import BackButton from "components/shared/BackButton";

export default function AddRecords() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(pageItemSchema),
  });

  // checkbox state
  const [checked, setChecked] = useState(false);

  const handleCheckChange = () => setChecked((state) => !state);

  // editor value
  const [text, setText] = useState("");

  // upload image and video state
  const [recordsImage, setRecordsImage] = useState({
    url: null,
    isChanged: false,
  });
  const [recordsVideo, setRecordsVideo] = useState({
    url: null,
    isChanged: false,
  });

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // select option state
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // add records
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!recordsImage.url) return Toast({ type: "empty", title: "فیلد عکس " });
    const res = await request({
      url: () =>
        addRecordRequest(
          {
            title: data.title,
            category_id: selectedSubCategory,
            text,
            show_in_home_page: checked ? 1 : 0,
            main_image: recordsImage,
            video: recordsVideo ? recordsVideo : null,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "افزودن سوابق" });
      navigate("/records");
    }
  };

  // uploaded image handler method
  const handleChangeRecordsImage = (val) => {
    validateImg(val);
    setRecordsImage({ url: val, isChanged: true });
  };

  // get subcategory
  const getRecordSubCategoryData = useCallback(async () => {
    const res = await request({
      url: getRecordSubCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });
    setLoading(false);
    if (res) {
      let transformedData = res.data?.data.map((items) => ({
        id: items.id,
        title: items.title,
      }));
      setSubCategoryList(transformedData);
    }
  }, [request]);

  useEffect(() => {
    getRecordSubCategoryData();
  }, [getRecordSubCategoryData]);

  // uploaded video handler method
  const handleChangeRecordsVideo = (val) =>
    setRecordsVideo({ url: val, isChanged: true });

  return (
    <DrawerCM title="افزودن سوابق">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/records" />
          </Grid>
          <StyledStack>
            <StyledTitle>افزودن سوابق</StyledTitle>
            <Grid container>
              <Grid item md={4} xs={12} p={1.2}>
                <TextField
                  label="عنوان سوابق"
                  variant="outlined"
                  fullWidth
                  id="title"
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                />
              </Grid>

              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="زیردسته"
                  control={control}
                  title="زیردسته"
                  id="subCategory"
                  {...register("subCategory")}
                  error={Boolean(errors.subCategory)}
                  helperText={errors.subCategory?.message}
                  onChange={(value) => setSelectedSubCategory(value)}
                >
                  {subCategoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={4} xs={12} p={1.2} sx={{ pt: 2.2 }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleCheckChange}
                      />
                    }
                    label="نمایش در صفحه اصلی"
                  />
                </FormGroup>
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {recordsImage.url ? (
                  <Upload
                    isWeb={recordsImage.isWeb}
                    isLocal={!recordsImage.isWeb}
                    file={recordsImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeRecordsImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={recordsImage.isChanged}
                    file={recordsImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeRecordsImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {recordsVideo.url ? (
                  <UploadVideoCM
                    isWeb={recordsVideo.isWeb}
                    isLocal={!recordsVideo.isWeb}
                    file={recordsVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeRecordsVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={recordsVideo.isChanged}
                    file={recordsVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeRecordsVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item xs={12} p={1.2}>
                <Editor
                  enableToolbar={true}
                  onChange={handleChangeText}
                  defaultValue={text}
                  setDefaultStyle="font-family: IranSans; height: 40vh;"
                  setOptions={{
                    buttonList: buttonList.complex,
                    font: [
                      "IranSans",
                      "IranSansBold",
                      "Arial",
                      "Impact",
                      "Tahoma",
                    ],
                  }}
                />
              </Grid>

              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
        </Grid>
      )}
    </DrawerCM>
  );
}
